import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CLUB from '../config/club.js'

// ── /score-mockup — DESIGN PREVIEW ONLY ──────────────────────────────────────
// Standalone presentational mockup of the match-play scorecard for Mark to
// review the layout. NO Firebase, NO live scores, NO writes — hardcoded demo
// state. The real live scorecard (src/score/ScoreApp.jsx) is untouched; this is
// a separate route (/score-mockup) so it can be viewed without a tournament.
//
// Matches the target mockup: light/cream page with WHITE cards, a boxed dark-
// green MATCH STATUS pill, a WON/HALVED/LOST/REMAINING counts row, white option
// cards with dark-green circular icons, and a next-hole footer. Tapping a result
// AUTO-ADVANCES to the next hole and updates the standing. PBCC theme (green on
// white/cream). When approved, this layout gets wired into the real card.

const GREEN = CLUB.primaryColor       // forest green (#123D26)
const PAGE = '#F2F1EA'                // soft cream page behind the cards
const CARD = '#FFFFFF'
const LINE = '#E4E6E0'                // hairline dividers / card borders
const MUTED = '#6B7A6E'               // gray sub-labels
const TINT = '#E7F0E8'                // selected-option tint / tap banner
const GREEN_MED = '#4F7A5E'           // "1 DOWN" style green text
const ON_GREEN = '#FFFFFF'

// Demo course pars (mockup only).
const PARS = [4, 3, 5, 4, 4, 4, 4, 3, 5, 4, 4, 3, 5, 4, 4, 4, 3, 5]
const TEAM_A = 'Ratliff / Keese'
const TEAM_B = 'James / Klein'

// ── Inline icons (white on the green circles; green line-art elsewhere) ──
const Trophy = ({ c = ON_GREEN }) => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 21h8M12 17v4M7 4h10v4a5 5 0 0 1-10 0V4z" />
    <path d="M17 5h2.5a1.5 1.5 0 0 1-1.5 3.2M7 5H4.5a1.5 1.5 0 0 0 1.5 3.2" />
  </svg>
)
const Equals = ({ c = ON_GREEN }) => (
  <svg width="26" height="26" viewBox="0 0 24 24" stroke={c} strokeWidth="2.6" strokeLinecap="round">
    <line x1="6" y1="9.5" x2="18" y2="9.5" /><line x1="6" y1="14.5" x2="18" y2="14.5" />
  </svg>
)
const Hand = () => (
  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 11.5V6.5a1.4 1.4 0 0 1 2.8 0v4.3" />
    <path d="M11.8 10.8V5a1.4 1.4 0 0 1 2.8 0v5.8" />
    <path d="M14.6 11V7a1.4 1.4 0 0 1 2.8 0v6a5.5 5.5 0 0 1-5.5 5.5h-1a4.5 4.5 0 0 1-3.6-1.8l-2.2-3a1.5 1.5 0 0 1 2.3-1.9l1.2 1.3" />
  </svg>
)
const Flag = () => (
  <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5.5 21V4" /><path d="M5.5 4.5h11l-2.2 3.2 2.2 3.2h-11" />
  </svg>
)
const DownArrow = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={GREEN} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" /><polyline points="6 13 12 19 18 13" />
  </svg>
)

export default function ScorecardMockup() {
  const navigate = useNavigate()
  // Demo: A is 1 UP through 6; now playing hole 7. Tapping a result advances.
  const [thru, setThru] = useState(6)
  const [up, setUp] = useState(1)          // A's lead (+) / deficit (−)
  const [won, setWon] = useState({ a: 3, b: 1, halved: 2 })

  const hole = thru + 1
  const par = PARS[(hole - 1) % 18]
  const nextHole = hole + 1
  const nextPar = PARS[(nextHole - 1) % 18]
  const remaining = Math.max(0, 18 - hole)

  const bigLead = up === 0 ? 'TIED' : `${up > 0 ? 'UP' : 'DOWN'} ${Math.abs(up)}`
  const leaderName = up > 0 ? TEAM_A : up < 0 ? TEAM_B : 'ALL SQUARE'
  const summaryLeft = up > 0 ? `${up} UP` : up < 0 ? `${-up} DOWN` : 'ALL SQ'
  const summaryRight = up < 0 ? `${-up} UP` : up > 0 ? `${up} DOWN` : 'ALL SQ'

  // AUTO-ADVANCE: tapping a result updates standing + counts and moves to the
  // next hole automatically (what the footer promises).
  const advance = (result) => {
    setUp((u) => (result === 'a' ? u + 1 : result === 'b' ? u - 1 : u))
    setWon((w) => ({ a: w.a + (result === 'a' ? 1 : 0), b: w.b + (result === 'b' ? 1 : 0), halved: w.halved + (result === 'halved' ? 1 : 0) }))
    setThru((t) => Math.min(18, t + 1))
  }

  const S = {
    page: { minHeight: '100vh', background: PAGE, color: GREEN, fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif', paddingBottom: 24 },
    header: { background: GREEN, color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px' },
    back: { display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', color: '#FFFFFF', fontSize: 17, fontWeight: 700, cursor: 'pointer' },
    hTitle: { fontSize: 19, fontWeight: 800 },
    burger: { background: 'transparent', border: 'none', color: '#FFFFFF', fontSize: 22, cursor: 'pointer' },
    wrap: { maxWidth: 480, margin: '0 auto', padding: '14px 14px 0' },
    card: { background: CARD, border: `1px solid ${LINE}`, borderRadius: 18, marginBottom: 14, overflow: 'hidden' },

    // Card 1 — status + summary + counts
    statusRow: { display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 6, padding: '14px 14px' },
    teamL: { display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 15 },
    teamR: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8, fontWeight: 800, fontSize: 15 },
    dotFill: { width: 13, height: 13, borderRadius: 999, background: GREEN, flexShrink: 0 },
    dotHollow: { width: 13, height: 13, borderRadius: 999, background: 'transparent', border: `2px solid ${GREEN}`, flexShrink: 0 },
    statusBox: { background: GREEN, color: '#FFFFFF', borderRadius: 14, padding: '8px 18px', textAlign: 'center', minWidth: 140 },
    stCaps: { fontSize: 10, fontWeight: 800, letterSpacing: 1.5, opacity: 0.85 },
    stName: { fontSize: 12, fontWeight: 800, letterSpacing: 0.5, marginTop: 1, textTransform: 'uppercase' },
    stBig: { fontSize: 30, fontWeight: 900, lineHeight: 1.05, margin: '1px 0' },
    stThru: { fontSize: 11, fontWeight: 800, letterSpacing: 1, opacity: 0.85 },

    rowDivide: { borderTop: `1px solid ${LINE}` },
    summary: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', textAlign: 'center', padding: '14px 6px' },
    sumCellL: { borderRight: `1px solid ${LINE}` },
    sumCellR: { borderLeft: `1px solid ${LINE}` },
    sumBig: { fontSize: 20, fontWeight: 900 },
    sumLbl: { fontSize: 11, fontWeight: 800, letterSpacing: 1, color: MUTED, marginTop: 2 },

    counts: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', textAlign: 'center', padding: '12px 4px' },
    countLbl: { fontSize: 11, fontWeight: 800, letterSpacing: 0.5, color: MUTED },
    countNum: { fontSize: 20, fontWeight: 900, marginTop: 3 },
    cDiv: { borderLeft: `1px solid ${LINE}` },

    // Card 2 — hole + options
    holeCard: { background: CARD, border: `1px solid ${LINE}`, borderRadius: 18, padding: '18px 16px 16px', marginBottom: 14, position: 'relative' },
    holeTitle: { textAlign: 'center', fontWeight: 900 },
    holeBig: { fontSize: 38, color: GREEN },
    holePar: { fontSize: 22, color: MUTED, fontWeight: 800 },
    holeSub: { textAlign: 'center', color: MUTED, fontWeight: 700, fontSize: 15, marginTop: 2, marginBottom: 14 },
    tapBanner: { display: 'flex', alignItems: 'center', gap: 14, background: '#ECEFE9', borderRadius: 14, padding: '14px 16px', marginBottom: 14 },
    tapTitle: { fontSize: 17, fontWeight: 900, color: GREEN },
    tapSub: { fontSize: 13, color: MUTED, fontWeight: 600, marginTop: 2 },
    tapArrow: { textAlign: 'center', color: GREEN, fontSize: 26, fontWeight: 900, lineHeight: 1, marginTop: -4, marginBottom: 8 },

    option: (selected) => ({ width: '100%', display: 'flex', alignItems: 'center', gap: 14, background: selected ? TINT : CARD, border: `${selected ? 2 : 1}px solid ${selected ? GREEN : LINE}`, borderRadius: 14, padding: '14px 16px', marginBottom: 12, cursor: 'pointer', textAlign: 'left' }),
    iconCircle: { width: 48, height: 48, borderRadius: 999, background: GREEN, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    optName: { fontSize: 19, fontWeight: 900, color: GREEN },
    optAction: { fontSize: 14, fontWeight: 700, color: MUTED, marginTop: 1 },
    scrollHint: { position: 'absolute', right: 14, bottom: 12, width: 40, height: 40, borderRadius: 999, background: '#FFFFFF', border: `1px solid ${LINE}`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.08)' },

    // Footer — next hole
    footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, background: CARD, border: `1px solid ${LINE}`, borderRadius: 18, padding: '14px 16px' },
    footLeft: { display: 'flex', alignItems: 'center', gap: 12 },
    footLbl: { fontSize: 11, fontWeight: 900, letterSpacing: 1.2, color: MUTED },
    footHole: { fontSize: 22, fontWeight: 900, color: GREEN, lineHeight: 1.1 },
    footPar: { fontSize: 13, color: MUTED, fontWeight: 700 },
    footRight: { display: 'flex', alignItems: 'center', gap: 8, textAlign: 'right' },
    footNote: { fontSize: 13, color: MUTED, fontWeight: 600, maxWidth: 150, lineHeight: 1.25 },
    chev: { fontSize: 26, fontWeight: 900, color: GREEN },
    done: { textAlign: 'center', padding: 40 },
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <button style={S.back} onClick={() => navigate(-1)}>‹ Back</button>
        <div style={S.hTitle}>Match Play</div>
        <button style={S.burger} aria-label="Menu">≡</button>
      </div>

      <div style={S.wrap}>
        {/* 1) STATUS BOX — always at the very top so who's up is always visible */}
        <div style={S.card}>
          <div style={S.statusRow}>
            <div style={S.teamL}><span style={S.dotFill} />{TEAM_A}</div>
            <div style={S.statusBox}>
              <div style={S.stCaps}>MATCH STATUS</div>
              <div style={S.stName}>{leaderName}</div>
              <div style={S.stBig}>{bigLead}</div>
              <div style={S.stThru}>THRU {thru}</div>
            </div>
            <div style={S.teamR}>{TEAM_B}<span style={S.dotHollow} /></div>
          </div>
        </div>

        {/* Reference stats card — summary + counts (moved BELOW the action) */}
        {(() => {
          const StatsCard = (
            <div style={S.card}>
              <div style={S.summary}>
                <div style={S.sumCellL}><div style={S.sumBig}>{summaryLeft}</div></div>
                <div><div style={S.sumBig}>{thru}</div><div style={S.sumLbl}>PLAYED</div></div>
                <div style={S.sumCellR}><div style={{ ...S.sumBig, color: GREEN_MED }}>{summaryRight}</div></div>
              </div>
              <div style={{ ...S.counts, ...S.rowDivide }}>
                <div><div style={S.countLbl}>WON</div><div style={S.countNum}>{won.a}</div></div>
                <div style={S.cDiv}><div style={S.countLbl}>HALVED</div><div style={S.countNum}>{won.halved}</div></div>
                <div style={S.cDiv}><div style={S.countLbl}>LOST</div><div style={S.countNum}>{won.b}</div></div>
                <div style={S.cDiv}><div style={S.countLbl}>REMAINING</div><div style={S.countNum}>{remaining}</div></div>
              </div>
            </div>
          )
          return hole <= 18 ? (
            <>
              {/* 2) HOLE + SCORING — high on the screen, no scrolling to score */}
              <div style={S.holeCard}>
                <div style={S.holeTitle}>
                  <span style={S.holeBig}>Hole {hole}</span> <span style={S.holePar}>| Par {par}</span>
                </div>
                <div style={S.holeSub}>Match Play</div>

                <div style={S.tapBanner}>
                  <Hand />
                  <div>
                    <div style={S.tapTitle}>Tap for results for this hole below</div>
                    <div style={S.tapSub}>Select winner, loser or halved</div>
                  </div>
                </div>

                {/* Down-arrow pointing to the option choices right below */}
                <div style={S.tapArrow} aria-hidden="true">↓</div>

                <button style={S.option(true)} onClick={() => advance('a')}>
                  <span style={S.iconCircle}><Trophy /></span>
                  <span><div style={S.optName}>{TEAM_A}</div><div style={S.optAction}>Win Hole</div></span>
                </button>
                <button style={S.option(false)} onClick={() => advance('halved')}>
                  <span style={S.iconCircle}><Equals /></span>
                  <span><div style={S.optName}>Halved</div><div style={S.optAction}>Halve Hole</div></span>
                </button>
                <button style={S.option(false)} onClick={() => advance('b')}>
                  <span style={S.iconCircle}><Trophy /></span>
                  <span><div style={S.optName}>{TEAM_B}</div><div style={S.optAction}>Win Hole</div></span>
                </button>

                <div style={S.scrollHint} aria-hidden="true"><DownArrow /></div>
              </div>

              {/* 3) STANDING / COUNTS SUMMARY — below the action */}
              {StatsCard}

              {/* 4) NEXT HOLE footer */}
              <div style={S.footer}>
                <div style={S.footLeft}>
                  <Flag />
                  <div>
                    <div style={S.footLbl}>NEXT HOLE</div>
                    <div style={S.footHole}>Hole {nextHole}</div>
                    <div style={S.footPar}>Par {nextPar}</div>
                  </div>
                </div>
                <div style={S.footRight}>
                  <div style={S.footNote}>After selection, advances automatically</div>
                  <span style={S.chev}>›</span>
                </div>
              </div>
            </>
          ) : (
            <>
              {StatsCard}
              <div style={S.done}>
                <div style={{ fontSize: 44 }}>🏁</div>
                <div style={{ fontSize: 22, fontWeight: 900, marginTop: 8, color: GREEN }}>Match complete</div>
                <div style={{ color: MUTED, marginTop: 6 }}>{leaderName} {bigLead} · thru {thru}</div>
              </div>
            </>
          )
        })()}
      </div>
    </div>
  )
}
